import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";
import { Reservation } from "../types/Reservation";
import { ReservaEstacionamientoDisplay } from "../../../types/ReservaEstacionamiento";
import { useBreakpoint } from "../../../hooks/useBreakpoint";

interface TooltipData {
  reserva: Reservation | ReservaEstacionamientoDisplay;
  position: {
    x: number;
    y: number;
  };
}

interface ReservationTooltipContextType {
  visible: boolean;
  tooltipData: TooltipData | null;
  showTooltip: (
    reserva: Reservation | ReservaEstacionamientoDisplay,
    position: { x: number; y: number }
  ) => void;
  hideTooltip: () => void;
}

const ReservationTooltipContext = createContext<
  ReservationTooltipContextType | undefined
>(undefined);

export const TooltipProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [visible, setVisible] = useState(false);
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);
  const enterTimeoutRef = useRef<NodeJS.Timeout>();
  const leaveTimeoutRef = useRef<NodeJS.Timeout>();
  const lastReservaRef = useRef<string | null>(null);
  const pendingTooltipRef = useRef<TooltipData | null>(null);

  // 🔥 OPTIMIZACIÓN: Usar hook centralizado para breakpoints
  const { isMobile } = useBreakpoint();

  const showTooltip = useCallback(
    (
      reserva: Reservation | ReservaEstacionamientoDisplay,
      position: { x: number; y: number }
    ) => {
      // No mostrar tooltip en dispositivos móviles
      if (isMobile) return;

      if (enterTimeoutRef.current) clearTimeout(enterTimeoutRef.current);
      if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);

      if (lastReservaRef.current === reserva.id) {
        setTooltipData((prev) => (prev ? { ...prev, position } : null));
        return;
      }

      pendingTooltipRef.current = { reserva, position };
      lastReservaRef.current = reserva.id;

      if (visible) {
        setVisible(false);
        setTooltipData(pendingTooltipRef.current);
        enterTimeoutRef.current = setTimeout(() => {
          setVisible(true);
        }, 400);
      } else {
        setTooltipData(pendingTooltipRef.current);
        enterTimeoutRef.current = setTimeout(() => {
          setVisible(true);
        }, 400);
      }
    },
    [isMobile, visible]
  );

  const hideTooltip = useCallback(() => {
    if (enterTimeoutRef.current) clearTimeout(enterTimeoutRef.current);
    if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);

    setVisible(false);
    setTooltipData(null);
    lastReservaRef.current = null;
    pendingTooltipRef.current = null;
  }, []);

  return (
    <ReservationTooltipContext.Provider
      value={{
        visible,
        tooltipData,
        showTooltip,
        hideTooltip,
      }}
    >
      {children}
    </ReservationTooltipContext.Provider>
  );
};

export const useReservationTooltip = () => {
  const context = useContext(ReservationTooltipContext);
  if (context === undefined) {
    throw new Error(
      "useReservationTooltip debe ser usado dentro de un TooltipProvider"
    );
  }
  return context;
};
